import { useEffect, useRef } from 'react';

import { diagnostics } from '@/utils/diagnostics';

type MaybePromise<T = unknown> = T | PromiseLike<T>;
type RefetchFn = () => MaybePromise<unknown>;

export interface UseRefetchOnAppResumeOptions {
  label?: string;
  enabled?: boolean;
  dedupeMs?: number;
}

/**
 * Hook that refetches data when the app comes back into focus after being backgrounded.
 * This helps recover from missed WebSocket subscription events that can occur when
 * browsers throttle background tabs.
 *
 * Listens to:
 * - visibilitychange: when tab becomes visible
 * - pageshow: when page is restored from BFCache
 *
 * Includes a short dedupe window to prevent double triggers.
 */
export function useRefetchOnAppResume(
  refetch: RefetchFn,
  opts?: UseRefetchOnAppResumeOptions
) {
  const {
    label = 'WorkflowTrackers',
    enabled = true,
    dedupeMs = 1000,
  } = opts ?? {};

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!enabled) return;

    let lastRefetchTime = 0;

    const trigger = (reason: 'visibilitychange' | 'pageshow') => {
      const now = Date.now();
      if (now - lastRefetchTime < dedupeMs) return;

      lastRefetchTime = now;
      diagnostics.debug(`[${label}] App resumed (${reason}), refetching`);
      try {
        void Promise.resolve(refetchRef.current()).catch(() => {});
      } catch {
        // ignore sync errors from refetch
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        trigger('visibilitychange');
      }
    };

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        trigger('pageshow');
      }
    };

    document.addEventListener('visibilitychange', onVisibility, {
      passive: true,
    });
    window.addEventListener('pageshow', onPageShow, { passive: true });

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled, label, dedupeMs]);
}
